// CCC (Conseil Café-Cacao) decision policy — kept in one place so the
// FE blocker, the Analyse panel's expiry chip, and any future report
// share the same arithmetic.
//
// Mirrors `transit.CCCBVValidityDays` on the backend. A BV is valid for
// 21 days from its issuance date; past that the OT can't advance until
// a fresh BV is issued.

import type { OTLotLink } from "@/features/transit/api/types"

export const CCC_BV_VALIDITY_DAYS = 21

// parseBVDate turns a yyyy-mm-dd / RFC3339 string into a Date anchored
// at UTC midnight. Returns null on missing or unparseable input — the
// caller decides whether "no date" means "not expired" or "missing".
export function parseBVDate(value: string | null | undefined): Date | null {
  if (!value) return null
  // Date-only ("2026-05-14") parses as UTC midnight, which is what we want;
  // anything more specific (RFC3339) Date already handles.
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return null
  return d
}

// bvExpiresAt is the inclusive expiry instant — the BV is valid up to
// and including that timestamp. Returns null when the issuance date is
// missing / unparseable.
export function bvExpiresAt(value: string | null | undefined): Date | null {
  const d = parseBVDate(value)
  if (!d) return null
  const expires = new Date(d.getTime())
  expires.setUTCDate(expires.getUTCDate() + CCC_BV_VALIDITY_DAYS)
  return expires
}

// isBVExpired mirrors the backend predicate. A missing date returns
// false so a legacy decision doesn't trigger a blocker until the new
// form is filled in.
export function isBVExpired(
  value: string | null | undefined,
  now: Date = new Date(),
): boolean {
  const expires = bvExpiresAt(value)
  if (!expires) return false
  return now.getTime() > expires.getTime()
}

// daysUntilExpiry returns a signed integer: positive = days remaining
// (rounded up), 0 = expires today, negative = days since expiry. Null
// when the issuance date is missing.
export function daysUntilExpiry(
  value: string | null | undefined,
  now: Date = new Date(),
): number | null {
  const expires = bvExpiresAt(value)
  if (!expires) return null
  const msPerDay = 24 * 60 * 60 * 1000
  const diffMs = expires.getTime() - now.getTime()
  return Math.ceil(diffMs / msPerDay)
}

// hasExpiredBV is the predicate used by the OT-wide blocker check. We
// only treat a BV as expired when (a) the decision was BV in the first
// place and (b) we have a date to anchor the window on.
export function hasExpiredBV(link: OTLotLink): boolean {
  if (link.cccKind !== "BV") return false
  return isBVExpired(link.cccBVDate)
}
